import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'aws-amplify/auth';
import NavigationRoutes from './app/routes/index.js';
import { useAutoTokenRefresh } from './shared/hooks/useAutoTokenRefresh';
import { NavigationHistoryProvider } from './shared/context/NavigationHistoryContext';

function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const { sessionReady } = useAutoTokenRefresh();

  // Multi-tab logout sync
  useEffect(() => {
    const handleStorageChange = async (event: StorageEvent) => {
      if (event.key === 'logout-event') {
        // User was logged out in another tab
        // Clear logout reason (reason stored for potential future use)
        localStorage.removeItem('logout-reason');

        try {
          await signOut();
        } catch (error) {
          console.warn('Error during sign out:', error);
        }

        // Only redirect if not already on login page
        if (location.pathname !== '/login') {
          navigate('/login', { replace: true });
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [navigate, location.pathname]);

  // Check for logout reason on mount (handles same-tab logout)
  useEffect(() => {
    const checkLogoutReason = async () => {
      const reason = localStorage.getItem('logout-reason');
      if (reason === 'ACCOUNT_DEACTIVATED' && location.pathname === '/login') {
        // Clear the reason after handling
        localStorage.removeItem('logout-reason');
      }
    };

    checkLogoutReason();
  }, [location.pathname]);

  return (
    <NavigationHistoryProvider>
      {sessionReady ? <NavigationRoutes /> : null}
    </NavigationHistoryProvider>
  );
}

export default App;
